import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AtSign, History, ArrowUp, Mic } from "lucide-react";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { primaryHover } from "shared/theme/trodoColors";

const ACCENT = theme.primaryLight;
const ACCENT_HOVER = primaryHover;
const BORDER = theme.border;
const CHAT_INPUT_BG = "#F5F0E8";

function readUserDisplayName() {
  try {
    const raw = localStorage.getItem("User");
    if (!raw) return "there";
    const user = JSON.parse(raw);
    const name = (user.name || "").trim();
    if (name) return name;
    const email = (user.email || "").trim();
    if (email) return email.split("@")[0] || "there";
    return "there";
  } catch {
    return "there";
  }
}

function getGreetingName(displayName) {
  if (!displayName || displayName === "there") return displayName;
  return displayName.trim().split(/\s+/)[0];
}

function getTimeGreeting() {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return "Good morning";
  if (h >= 12 && h < 17) return "Good afternoon";
  return "Good evening";
}

/**
 * Fixed gateway strip on team home: time-based greeting + single-line style composer.
 * Submit navigates to chat with location state — ChatPage opens a new thread and sends the message.
 */
export default function HomeChatGateway() {
  const { team } = useParams();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState(() => readUserDisplayName());
  const [input, setInput] = useState("");
  const textareaRef = useRef(null);

  const canDictate =
    typeof window !== "undefined" &&
    Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);

  useEffect(() => {
    setDisplayName(readUserDisplayName());
    const onStorage = (e) => {
      if (e.key === "User") setDisplayName(readUserDisplayName());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 44), 200)}px`;
  }, [input]);

  const goChat = () => {
    if (team) navigate(`/${team}/chat`);
  };

  const submit = () => {
    const t = input.trim();
    if (!t || !team) return;
    const slug = encodeURIComponent(team);
    navigate(`/${slug}/chat`, { state: { pendingHomeMessage: t } });
  };

  const onKeyDown = (e) => {
    if (e.key !== "Enter") return;
    if (e.shiftKey) return;
    e.preventDefault();
    submit();
  };

  const canSend = Boolean(input.trim() && team);

  return (
    <header className="shrink-0 w-full bg-surface" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className="w-full max-w-3xl lg:max-w-4xl mx-auto px-4 sm:px-6 pt-16 pb-10 sm:pb-12 md:pb-14 sm:pt-20 md:pt-24 lg:pt-28">
        <div className="flex flex-col items-center text-center w-full">
          <div className="flex flex-col items-center gap-2">
            <p className="text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight" style={{ color: theme.textPrimary }}>
              {getTimeGreeting()}, {getGreetingName(displayName)}!
            </p>
            <p className="text-base sm:text-lg md:text-xl font-normal tracking-tight" style={{ color: theme.textSecondary }}>
              What do you want to automate?
            </p>
          </div>

          <div className="w-full mt-12 sm:mt-14 md:mt-16">
            <div
              className="w-full rounded-[24px] border shadow-sm overflow-visible transition-shadow focus-within:shadow-md"
              style={{ borderColor: BORDER, backgroundColor: CHAT_INPUT_BG }}
            >
              <div className="flex items-end gap-1.5 sm:gap-2 pl-2 sm:pl-3 pr-2 py-2 min-h-[52px]">
                <span className="shrink-0 self-center text-[13px] sm:text-sm opacity-35 select-none pb-0.5" aria-hidden>
                  ↵
                </span>
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Ask Lucid about your analytics…"
                  rows={1}
                  className="flex-1 min-w-0 min-h-[44px] max-h-[200px] resize-none bg-transparent border-0 shadow-none text-[15px] sm:text-base py-2.5 placeholder:text-[color:var(--text-disabled)] outline-none ring-0 focus:outline-none focus:ring-0 text-left"
                  style={{ caretColor: ACCENT, color: theme.textPrimary }}
                  aria-label="Ask a question"
                />
                <div className="flex items-center gap-1 shrink-0 pb-0.5">
                  <button
                    type="button"
                    className="w-9 h-9 rounded-xl border flex items-center justify-center transition-colors"
                    style={{
                      borderColor: BORDER,
                      backgroundColor: "rgba(255,255,255,0.5)",
                      color: theme.textPrimary,
                    }}
                    aria-label="Open chat to attach context"
                    title="Attach (@)"
                    onClick={goChat}
                  >
                    <AtSign size={18} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    className="w-9 h-9 rounded-xl border flex items-center justify-center transition-colors"
                    style={{
                      borderColor: BORDER,
                      backgroundColor: "rgba(255,255,255,0.5)",
                      color: theme.textPrimary,
                    }}
                    aria-label="Open chat history"
                    title="History"
                    onClick={goChat}
                  >
                    <History size={18} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${
                      canDictate ? "" : "cursor-not-allowed opacity-50"
                    }`}
                    style={{
                      borderColor: BORDER,
                      backgroundColor: "rgba(255,255,255,0.5)",
                      color: canDictate ? theme.textPrimary : theme.textMuted,
                    }}
                    aria-label="Voice (opens chat)"
                    title={canDictate ? "Voice: continue in chat" : "Voice not supported"}
                    onClick={goChat}
                  >
                    <Mic size={18} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    onClick={submit}
                    disabled={!canSend}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full text-ink-onBrand transition-colors disabled:opacity-35 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[color:rgb(var(--trodo-primary-rgb)/0.4)]"
                    style={{ backgroundColor: ACCENT }}
                    onMouseEnter={(e) => {
                      if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = ACCENT_HOVER;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = ACCENT;
                    }}
                    aria-label="Continue in chat"
                  >
                    <ArrowUp size={18} strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            </div>
            <p className="mt-2 text-center text-[11px] sm:text-xs" style={{ color: theme.textMuted }}>
              Enter to send · Shift+Enter for a new line
            </p>
          </div>
        </div>
      </div>
    </header>
  );
}
